import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import {
  Alert,
  Breadcrumb,
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  List,
  notification,
  Pagination,
  Row,
  Select,
  Space,
  Table,
  Tag,
} from "antd";
import { CaretDownOutlined, PlusCircleOutlined, SearchOutlined } from "@ant-design/icons";
import moment from "moment";
import { BrowserView, MobileView } from "react-device-detect";
import PageLayout from "./PageLayout";
import PageTitle from "./PageTitle";
import FinDocStatusObj from "../helper/FinDocStatusObj";
import { FinDocCompanyObj, FinDocCompanyOptions, FinDocDocTypeObj } from "../helper/FinDocDocTypeObj";
import { getFinDocSitActor, getFinDocSitConfig } from "../newApi/findoc.mock";

const statusColorMap = {
  pendingAudit: "processing",
  pendingMgr: "gold",
  pendingFinance: "cyan",
  done: "success",
  reject: "error",
  cancel: "default",
};

const isMichaelApprover = (value) => {
  const normalized = String(value || "").toLowerCase();
  return normalized === "michael.hsia@wiadvance.com" || normalized.includes("michael hsia") || normalized.includes("夏志豪");
};

const getDisplayAuditorName = (record) => (
  record.findoc_status === "pendingMgr" || isMichaelApprover(record.next_auditor_mail) || isMichaelApprover(record.next_auditor_name)
    ? "總經理"
    : record.next_auditor_name || "-"
);

export default function FinDocListPage({
  title,
  menuKey,
  defaultStatuses,
  fetcher,
  addPath,
  getActions,
  extraFilters,
}) {
  const { Option } = Select;
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { currentUser } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const pageIndex = Number(searchParams.get("p") || 1);
  const pageSize = Number(searchParams.get("ps") || 10);
  const sitConfig = getFinDocSitConfig();
  const actor = useMemo(() => {
    if (sitConfig.enabled) {
      return getFinDocSitActor();
    }

    return {
      name: currentUser?.name,
      email: currentUser?.email,
    };
  }, [currentUser?.email, currentUser?.name, sitConfig.enabled]);
  const [filters, setFilters] = useState({
    findoc_status: defaultStatuses,
  });

  useEffect(() => {
    localStorage.setItem("currentMenuItemsKey", menuKey);
    form.setFieldsValue({
      findoc_status: defaultStatuses,
    });
  }, [defaultStatuses, form, menuKey]);

  useEffect(() => {
    setLoading(true);
    fetcher({
      ...filters,
      ...(extraFilters ? extraFilters(actor) : {}),
      page: pageIndex,
      page_record_set: pageSize,
    })
      .then((resp) => {
        if (resp?.isSuccess !== false) {
          setData(resp?.data || []);
          setTotal(resp?.pageCounts || 0);
          return;
        }

        setData([]);
        setTotal(0);
        notification.error({ message: "FinDoc 查詢失敗", description: resp?.message || "請稍後再試或聯絡系統管理員。" });
      })
      .catch(() => {
        setData([]);
        setTotal(0);
        notification.error({ message: "FinDoc 查詢失敗", description: "無法取得財務文件資料。" });
      })
      .finally(() => setLoading(false));
  }, [actor, extraFilters, fetcher, filters, pageIndex, pageSize]);

  const onSearch = () => {
    const values = form.getFieldsValue();
    const nextFilters = {
      findoc_no: values.findoc_no ? Number(values.findoc_no) : null,
      applicant_name: values.applicant_name || null,
      company: values.company || null,
      findoc_status: values.findoc_status?.length ? values.findoc_status : defaultStatuses,
      start_date: values.date_range?.[0] ? values.date_range[0].startOf("day").valueOf() : null,
      end_date: values.date_range?.[1] ? values.date_range[1].endOf("day").valueOf() : null,
    };

    searchParams.set("p", 1);
    setSearchParams(searchParams);
    setFilters(nextFilters);
  };

  const onReset = () => {
    form.resetFields();
    form.setFieldsValue({
      findoc_status: defaultStatuses,
    });
    searchParams.set("p", 1);
    setSearchParams(searchParams);
    setFilters({ findoc_status: defaultStatuses });
  };

  const renderActions = (record) => {
    const actions = getActions(record, actor, navigate);

    return (
      <Space split={<Divider type="vertical" />} wrap>
        {actions.map((action) => (
          <a key={action.label} onClick={action.onClick} style={action.danger ? { color: "#cf1322" } : undefined}>
            {action.label}
          </a>
        ))}
      </Space>
    );
  };

  const columns = [
    {
      title: "功能",
      dataIndex: "action",
      key: "action",
      align: "center",
      width: 140,
      render: (_, record) => renderActions(record),
    },
    {
      title: "編號",
      dataIndex: "findoc_no",
      key: "findoc_no",
      width: 110,
      render: (value) => `FD-${value}`,
    },
    {
      title: "申請人",
      dataIndex: "applicant_name",
      key: "applicant_name",
      width: 140,
    },
    {
      title: "公司別",
      dataIndex: "company",
      key: "company",
      width: 180,
      render: (value) => FinDocCompanyObj[value] || value,
    },
    {
      title: "申請項目",
      dataIndex: "doc_items",
      key: "doc_items",
      render: (items) => {
        return (items || []).map((item) => FinDocDocTypeObj[item.doc_type] || item.doc_type).join("、");
      },
    },
    {
      title: "申請日期",
      dataIndex: "cr_date",
      key: "cr_date",
      width: 140,
      render: (value) => (value ? moment(value).format("YYYY/MM/DD") : ""),
    },
    {
      title: "目前關卡",
      dataIndex: "next_auditor_name",
      key: "next_auditor_name",
      width: 180,
      render: (value, record) => (record.findoc_status === "done" || record.findoc_status === "reject" || record.findoc_status === "cancel" ? "-" : getDisplayAuditorName(record)),
    },
    {
      title: "狀態",
      dataIndex: "findoc_status",
      key: "findoc_status",
      width: 150,
      render: (value) => <Tag color={statusColorMap[value] || "default"}>{FinDocStatusObj[value] || value}</Tag>,
    },
    ...(menuKey === "FinDocArchived"
      ? [
          {
            title: "處理人",
            dataIndex: "done_by_name",
            key: "done_by_name",
            width: 140,
            render: (value) => value || "-",
          },
          {
            title: "歸檔日期",
            dataIndex: "done_date",
            key: "done_date",
            width: 140,
            render: (value) => (value ? moment(value).format("YYYY/MM/DD") : "-"),
          },
        ]
      : []),
  ];

  const getColumnTitle = (dataIndex) => columns.find((column) => column.dataIndex === dataIndex)?.title;

  const renderMobileList = () => (
    <Col span={24}>
      <List
        dataSource={data}
        loading={loading}
        style={{ background: "white" }}
        renderItem={(record) => (
          <List.Item style={{ display: "block", padding: 0, border: 0 }}>
            <Row style={{ padding: "10px 0", background: "#71AA1F" }}>
              <Col span={10} style={{ fontSize: 18, fontWeight: "bold", paddingLeft: 16, color: "white" }}>
                {getColumnTitle("findoc_no")}
              </Col>
              <Col span={14} style={{ fontSize: 18, fontWeight: "bold", paddingRight: 16, color: "white", textAlign: "right" }}>
                FD-{record.findoc_no}
              </Col>
            </Row>
            <Row gutter={[0, 14]} style={{ padding: "10px 16px 0 16px" }}>
              <Col span={10}>{getColumnTitle("applicant_name")}</Col>
              <Col span={14}>{record.applicant_name || "-"}</Col>
              <Col span={10}>{getColumnTitle("company")}</Col>
              <Col span={14}>{FinDocCompanyObj[record.company] || record.company || "-"}</Col>
              <Col span={10}>{getColumnTitle("doc_items")}</Col>
              <Col span={14}>
                {(record.doc_items || []).map((item) => FinDocDocTypeObj[item.doc_type] || item.doc_type).join(", ") || "-"}
              </Col>
              <Col span={10}>{getColumnTitle("cr_date")}</Col>
              <Col span={14}>{record.cr_date ? moment(record.cr_date).format("YYYY/MM/DD") : "-"}</Col>
              <Col span={10}>{getColumnTitle("next_auditor_name")}</Col>
              <Col span={14}>
                {record.findoc_status === "done" || record.findoc_status === "reject" || record.findoc_status === "cancel" ? "-" : getDisplayAuditorName(record)}
              </Col>
              <Col span={10}>{getColumnTitle("findoc_status")}</Col>
              <Col span={14}>
                <Tag color={statusColorMap[record.findoc_status] || "default"}>{FinDocStatusObj[record.findoc_status] || record.findoc_status}</Tag>
              </Col>
              {menuKey === "FinDocArchived" ? (
                <>
                  <Col span={10}>{getColumnTitle("done_by_name")}</Col>
                  <Col span={14}>{record.done_by_name || "-"}</Col>
                  <Col span={10}>{getColumnTitle("done_date")}</Col>
                  <Col span={14}>{record.done_date ? moment(record.done_date).format("YYYY/MM/DD") : "-"}</Col>
                </>
              ) : null}
              <Divider style={{ margin: 0 }} />
              <Row style={{ width: "100%" }} justify="space-evenly">
                {renderActions(record)}
              </Row>
              <Divider style={{ margin: "0 0 16px 0" }} />
            </Row>
            <Row style={{ background: "#f6f6f6", height: 20, margin: "0 0 16px 0" }} />
          </List.Item>
        )}
      />
      <Pagination
        size="small"
        current={pageIndex}
        pageSize={pageSize}
        total={total}
        showSizeChanger
        pageSizeOptions={[5, 10, 25, 50, 100]}
        style={{ display: "flex", justifyContent: "center", margin: "12px 0 24px" }}
        onChange={(page, size) => {
          searchParams.set("p", page);
          searchParams.set("ps", size);
          setSearchParams(searchParams);
          window.scrollTo(0, 0);
        }}
      />
    </Col>
  );

  return (
    <PageLayout spinning={loading}>
      <Col span={24}>
        <Breadcrumb separator=">">
          <Breadcrumb.Item href="/">WiAdvance EIP</Breadcrumb.Item>
          <Breadcrumb.Item href="/FinDoc/Application">財務文件申請</Breadcrumb.Item>
          <Breadcrumb.Item>{title}</Breadcrumb.Item>
        </Breadcrumb>
      </Col>
      <PageTitle>{title}</PageTitle>
      {sitConfig.enabled ? (
        <Col span={24} style={{ marginBottom: 16 }}>
          <Alert
            type="warning"
            showIcon
            message={`FinDoc SIT 模式：${actor.name} (${actor.email})`}
            description="此區塊使用前端模擬資料與狀態機，不會呼叫實際 FinDoc 後端。"
          />
        </Col>
      ) : null}
      <Col
        span={24}
        style={{
          backgroundColor: "white",
          margin: "10px 0",
          padding: "10px 15px",
        }}
      >
        <Form form={form} autoComplete="false" labelCol={{ span: 24 }}>
          <Row gutter={[16, 8]} align="bottom">
            <Col xs={24} md={6}>
              <Form.Item name="findoc_no" label="申請單號">
                <Input placeholder="輸入單號" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="applicant_name" label="申請人">
                <Input placeholder="輸入申請人" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="company" label="公司別">
                <Select allowClear placeholder="全部" options={FinDocCompanyOptions} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="date_range" label="申請日期">
                <DatePicker.RangePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={16}>
              <Form.Item name="findoc_status" label="狀態">
                <Select
                  mode="multiple"
                  allowClear
                  options={Object.entries(FinDocStatusObj)
                    .filter(([key]) => ["pendingAudit", "pendingMgr", "pendingFinance", "done", "reject", "cancel"].includes(key))
                    .map(([value, label]) => ({ value, label }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Space wrap style={{ display: "flex", justifyContent: "flex-end", marginBottom: 24, padding: "7px", gap: 8 }}>
                <Button icon={<SearchOutlined />} type="primary" style={{ height: "40.14px", margin: "5px" }} onClick={onSearch}>查詢</Button>
                <Button style={{ height: "40.14px", margin: "5px" }} onClick={onReset}>清除</Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Col>
      {addPath ? (
        <>
          <BrowserView>
            <Col span={24}>
              <Row justify="space-between" style={{ margin: "10px 10px 16px" }} gutter={[24, 8]}>
                <Col span={20} />
                <Col span={4}>
                  <Button
                    type="primary"
                    style={{ width: "100%", height: "40.14px", backgroundColor: "#00a2dd" }}
                    onClick={() => navigate(addPath)}
                  >
                    新增申請<PlusCircleOutlined />
                  </Button>
                </Col>
              </Row>
            </Col>
          </BrowserView>
          <MobileView>
            <Col span={24}>
              <Row justify="end" style={{ margin: "10px 10px 16px" }}>
                <Col span={12}>
                  <Button type="primary" style={{ width: "100%", height: "40.14px", backgroundColor: "#00a2dd" }} onClick={() => navigate(addPath)}>
                    新增申請<PlusCircleOutlined />
                  </Button>
                </Col>
              </Row>
            </Col>
          </MobileView>
        </>
      ) : null}
      <BrowserView>
        <Col span={24}>
          <Table
            rowKey={(record) => record.findoc_no}
            pagination={{
              position: ["topRight", "bottomRight"],
              total,
              pageSize,
              current: pageIndex,
            }}
            columns={columns}
            dataSource={data}
            onChange={(page) => {
              searchParams.set("p", page.current);
              searchParams.set("ps", pageSize);
              setSearchParams(searchParams);
              window.scrollTo(0, 0);
            }}
            footer={() => (
              <Col flex="auto">
                <div style={{ textAlign: "end", margin: "8px", color: "#7C7C7C" }}>
                  共 {total} 筆資料，每頁顯示
                  <Select
                    value={pageSize}
                    style={{
                      width: pageSize < 100 ? 60 : 70,
                      color: "#a5a5a5",
                    }}
                    bordered={false}
                    suffixIcon={<CaretDownOutlined />}
                    onChange={(value) => {
                      searchParams.set("p", 1);
                      searchParams.set("ps", value);
                      setSearchParams(searchParams);
                      window.scrollTo(0, 0);
                    }}
                  >
                    <Option value={5}>5</Option>
                    <Option value={10}>10</Option>
                    <Option value={25}>25</Option>
                    <Option value={50}>50</Option>
                    <Option value={100}>100</Option>
                  </Select>
                  筆
                </div>
              </Col>
            )}
          />
        </Col>
      </BrowserView>
      <MobileView style={{ marginBottom: "115px" }}>
        {renderMobileList()}
      </MobileView>
    </PageLayout>
  );
}
